/**
 * 회차 넘김 — **서버가 정한 회차를 표시만** 한다 (261001-client-works-report).
 * ★ 화면이 날짜를 계산하지 않는다. 경계는 구독일에서 나오므로 서버가 정해야
 *   "창은 한 곳에서만 정한다" 가 유지된다 — 화면이 from/to 를 보내면 그 규칙이 샌다.
 * ★ **다음 회차는 현재에서 막힌다.** 아직 오지 않은 기간을 열 이유가 없다.
 */
import type { works } from '@geo-admin/shared/contract/api/index.ts';
import type { z } from 'zod';

type Cycle = z.infer<typeof works.getWorks.response>['cycle'];

const md = (iso: string) => `${Number(iso.slice(5, 7))}/${Number(iso.slice(8, 10))}`;

/**
 * 넘김 버튼. ★ **누를 곳이 글자가 아니라 네모다** — 꺾쇠 한 글자는 너비가 6px 남짓이라
 * 마우스로도 빗나가고 손가락으로는 거의 못 누른다(2026-10-01 운영자 지적). 40px 정사각이면
 * 모바일 권장치(44px)에 근접하고, 테두리가 없어도 hover 바탕으로 누를 곳임이 드러난다.
 */
function Arrow({ dir, onClick, disabled }: { dir: 'prev' | 'next'; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-2xl leading-none transition-colors enabled:text-slate-500 enabled:hover:bg-slate-100 enabled:hover:text-slate-900 disabled:text-slate-200"
      aria-label={dir === 'prev' ? '이전 회차' : '다음 회차'}
    >
      {dir === 'prev' ? '‹' : '›'}
    </button>
  );
}

export function CyclePicker({
  cycle,
  back,
  onBack,
}: {
  cycle: Cycle | undefined;
  back: number;
  onBack: (next: number) => void;
}) {
  return (
    <div className="flex items-center gap-1">
      <Arrow dir="prev" onClick={() => onBack(back + 1)} />
      <div className="px-1 text-center">
        {/* 값이 아직 없어도 **자리를 지킨다** — 버튼이 좌우로 뛰지 않게.
            ★ 이 화면에서 **기간은 여기 하나뿐**이라(측정 세그먼트를 없앴다) 눈에 먼저 들어와야 한다 */}
        <div className="min-w-[112px] font-bold text-[19px] text-slate-900 leading-tight tabular-nums">
          {cycle?.label ?? '—'}
        </div>
        <div className="text-[13px] text-slate-500 tabular-nums">
          {cycle ? `${md(cycle.from)} ~ ${md(cycle.to)}` : ' '}
        </div>
      </div>
      <Arrow dir="next" onClick={() => onBack(Math.max(0, back - 1))} disabled={back === 0} />
    </div>
  );
}
